import _ from 'lodash';
import { Dispatch } from 'react';
import { FlowConnector, FlowItem } from '../../../../_components/flow-builder/TypeDefs';
import { Orientation } from '../../../../_components/flow-builder/canvas-element/Orientation';
import { CreateCEMRequest, CreateCEMResponse, GetCompensatoryEventMapDetails } from '../../../../store/_api/TypeDefs';
import { CompensatoryEventMap, EventMapDetails, FlowItemConditions, FlowItemDataMap, LoaderHook, PayloadTransformation, ServiceTypes, ToastMessageHook, TranslateHook, FlowConnectorButtonActions } from '../TypeDefs';

import { getServiceTypeIcon } from '../../../../_components/journey-builder-footer/utility/Helpers';
import { journeyCanvasConstants } from '../../../../constants/JourneyCanvas';
import { createCompensatoryEventMap, deleteCompensatoryMap, updateCompensatoryEventMap } from '../../../../store/_api';
import { Actions, UpdateActions } from '../../reducer/TypeDefs';
import { findEmptyPositionOnCanvas } from './CanvasHelpers';
import { applyConnectorConditionalExpressionRules } from './AndMergeForkHelpers';
import { checkBusinessToBusinessConnector, generateTooltipTextForConnector, getFlowConnectorButtons, getFlowConnectorConfig } from './ConnectorsHelpers';
import { getFlowItemButtons, getFlowItemConfig } from './FlowItemsHelpers';
import { createEventMapDetail, createEventMapEdges, createEventMapNodes, translateLabel } from './PrepareJourneyHelpers';
import eventSchemaIcon from '../../../../assets/images/EventSchema.svg';

/**
 * @method createCEMNodes
 * @description create flow item nodes for compensatory event map
 * @param node nodes
 * @param compEventMaps compensatory event map
 * @param itemEventMap flow item data map
 * @param readOnly if canvas is in view mode
 */
export const createCEMNodes = ({
  node,
  itemEventMap,
  flowItems,
  nodeId,
  readOnly
}: {
  node: GetCompensatoryEventMapDetails,
  itemEventMap: FlowItemDataMap,
  flowItems: FlowItem[],
  nodeId: string,
  readOnly: boolean,
}) => {
  // create compensatory event map nodes
  node.eventMapNodes?.forEach((compNode) => {
    const isExternal = compNode.serviceType?.toLowerCase() === ServiceTypes.EXTERNAL;

    const convergenceEdge = node.eventMapEdges?.find((edge) => edge.ingress.length > 0 && edge.egress.length === 1 && edge.egress[0].nodeId === compNode.nodeId);
    const conditionText = convergenceEdge?.ingressOperation;
    const item: FlowItem = {
      nodeId: compNode.nodeId,
      name: compNode.serviceName,
      description: compNode.serviceDescription,
      buttons: getFlowItemButtons(false, false, conditionText === FlowItemConditions.AND || conditionText === FlowItemConditions.T_AND, readOnly),
      icon: getServiceTypeIcon(compNode.serviceType),
      selectedIcon: getServiceTypeIcon(compNode.serviceType),
      isRoot: compNode.nodeId === nodeId,
      config: getFlowItemConfig({ isExternal }),
      x: compNode.xCoordinate,
      y: compNode.yCoordinate,
      subTitle: compNode.serviceOperationName,
      displayVersion: compNode.serviceVersion,
      isPayloadTransformationDataSelected: (conditionText === FlowItemConditions.AND || conditionText === FlowItemConditions.T_AND) && !!convergenceEdge?.payloadTransformations?.length,
    };
    flowItems.push(item);
    itemEventMap[compNode.nodeId] = {
      nodeId: compNode.nodeId,
      serviceName: compNode.serviceName,
      ingressEvent: compNode.eventName,
      serviceOperationName: compNode.serviceOperationName,
      serviceDescription: compNode.serviceDescription,
      serviceType: compNode.serviceType ?? ServiceTypes.BUSINESS,
      isExternal,
      isCompensateAction: false,
      isSaved: true,
      serviceVersion: compNode.serviceVersion ?? 1,
      ingressEventVersion: compNode.eventVersion ?? 1,
      condExpr: {},
      compensatoryEventMapName: compNode.compensatoryEventMapName,
      external: {}
    };
  });
};

/**
 * @method createCEMEdges
 * @description create edges for compensatory event map
 * @param node nodes
 * @param compEventMaps compensatory event map
 * @param itemEventMap flow item data map
 * @param readOnly if canvas is in view mode
 */
export const createCEMEdges = ({
  node,
  itemEventMap,
  flowConnectors,
  flowItems,
  readOnly,
  payloadTransformations = []
}: {
  node: GetCompensatoryEventMapDetails,
  itemEventMap: FlowItemDataMap,
  flowConnectors: FlowConnector[],
  flowItems: FlowItem[],
  readOnly: boolean,
  payloadTransformations: PayloadTransformation[]
}) => {
  // create compensatory event map edges
  node.eventMapEdges?.forEach((compEdge) => {
    if (compEdge.payloadTransformations) {
      payloadTransformations.push(...compEdge.payloadTransformations);
    }
    compEdge.ingress.forEach((i) => {
      compEdge.egress.forEach((e) => {
        const convergingConnectors = flowConnectors.filter((c) => c.toNodeId === e.nodeId);
        let convergence = false;

        // Detect convergence - if there are existing connectors pointing to the same target
        if (convergingConnectors.length > 0) {
          convergence = true;
          // Add delete buttons to ALL existing connectors that point to this target
          convergingConnectors.forEach((c) => {
            if (!c.buttons.find((b) => b.id === FlowConnectorButtonActions.DELETE)) {
              c.buttons = getFlowConnectorButtons(
                true,
                readOnly,
                checkBusinessToBusinessConnector(itemEventMap[c.fromNodeId].serviceType, itemEventMap[e.nodeId].serviceType),
                true,
                true
              );
            }
          });
        }

        flowConnectors.push({
          fromNodeId: i.nodeId,
          toNodeId: e.nodeId,
          orientation: {
            from: Orientation.HORIZONTAL,
            to: Orientation.HORIZONTAL
          },
          text: itemEventMap[e.nodeId].ingressEvent,
          displayEventVersion: itemEventMap[e.nodeId].ingressEventVersion,
          tooltip: generateTooltipTextForConnector(itemEventMap[e.nodeId].ingressEvent, !!e.mep, e.external),
          config: getFlowConnectorConfig({
            hasCondExpr: !!e.conditionMap && !!e.conditionMap[i.nodeId],
            mep: !!e.mep,
            external: e.external,
          }),
          buttons: getFlowConnectorButtons(
            convergence,
            readOnly,
            checkBusinessToBusinessConnector(itemEventMap[i.nodeId].serviceType, itemEventMap[e.nodeId].serviceType),
            true,
            true
          )
        });
        itemEventMap[e.nodeId].condExpr = e.conditionMap ?? {};
        itemEventMap[e.nodeId].external = { ...itemEventMap[e.nodeId].external, [`${i.nodeId}_${e.nodeId}`]: e.external };
      });
    });
    if (compEdge.ingressOperation && compEdge.egress.length > 0) {
      const egressNode = flowItems.find((fi) => fi.nodeId === compEdge.egress[0].nodeId);
      if (egressNode) {
        egressNode.conditionText = compEdge.ingressOperation;
        egressNode.config = getFlowItemConfig({ config: egressNode.config, conditionText: egressNode.conditionText });
      }
    }
  });
  const andMergeNodeIds = new Set<string>();
  node.eventMapEdges?.forEach((compEdge) => {
    if (compEdge.ingressOperation === FlowItemConditions.AND && compEdge.egress?.[0]?.nodeId) {
      andMergeNodeIds.add(compEdge.egress[0].nodeId);
    }
  });
  const appliedConnectors = applyConnectorConditionalExpressionRules(
    flowConnectors,
    andMergeNodeIds,
    flowItems.map(({ nodeId, conditionText }) => ({ nodeId, conditionText })),
    itemEventMap,
    readOnly,
    true
  );
  flowConnectors.length = 0;
  flowConnectors.push(...appliedConnectors);
};

/**
 * @method createCEM
 * @description create nodes and edges compensatory event map
 * @param node nodes
 * @param compEventMaps compensatory event map
 * @param itemEventMap flow item data map
 * @param readOnly if canvas is in view mode
 */
export const createCEM = ({
  node,
  itemEventMap,
  nodeId,
  readOnly
}: {
  node: GetCompensatoryEventMapDetails,
  itemEventMap: FlowItemDataMap,
  nodeId: string,
  readOnly: boolean
}): CompensatoryEventMap => {
  // create compensatory event map flowItems
  const flowItems: FlowItem[] = [];
  const flowConnectors: FlowConnector[] = [];
  const payloadTransformations: PayloadTransformation[] = [];

  // create CEM nodes
  createCEMNodes({ node, itemEventMap, flowItems, nodeId, readOnly });

  const firstNodeData = itemEventMap[nodeId];
  if (firstNodeData) {
    // create edge from selected service
    const firstIngressNodeId = node.eventMapNodes[0]?.nodeId ?? '';
    flowConnectors.push({
      fromNodeId: nodeId,
      toNodeId: firstIngressNodeId ?? '',
      orientation: {
        from: Orientation.VERTICAL,
        to: Orientation.VERTICAL
      },
      text: node.eventMapNodes[0].eventName,
      displayEventVersion: node.eventMapNodes[0].eventVersion,
      tooltip: generateTooltipTextForConnector(node.eventMapNodes[0].eventName, false, false),
      config: {},
      buttons: [{
        icon: eventSchemaIcon,
        id: FlowConnectorButtonActions.VIEW_EVENT_SCHEMA,
        label: translateLabel('labels.view_event_schema'),
        height: journeyCanvasConstants.flowItemBtnHeight,
        width: journeyCanvasConstants.flowItemBtnHeight,
      }]
    });
  }

  // create CEM Edges
  createCEMEdges({ node, itemEventMap, flowConnectors, flowItems, readOnly, payloadTransformations });

  return {
    compensatoryEventMapName: node.eventMapName,
    ingressNodeId: nodeId,
    flowItems,
    flowConnectors,
    payloadTransformations
  };
};

/**
 * @method prepareJourneyFromCEMData
 * @description Prepare data for FlowBuilder to render the journey to canvas
 * @param data CreateCompensatoryEventMapResponse
 * @param readOnly if canvas is in view mode
 * @returns FlowItems and FlowConnectors
 */
export const prepareCEMFromData = (data: CreateCEMResponse, readOnly: boolean): {
  items: FlowItem[],
  connectors: FlowConnector[],
  itemDataMap: FlowItemDataMap,
  rootNodeId: string,
  isRootExternal: boolean,
} => {
  const itemEventMap: FlowItemDataMap = {};
  const eventMapDetails = {
    eventMapFirstNodeId: data.compensatoryEventMapDetails.eventMapNodes[0].nodeId,
    eventMapEdges: data.compensatoryEventMapDetails.eventMapEdges,
    eventMapNodes: data.compensatoryEventMapDetails.eventMapNodes,
    eventMapMaxExecutionTimeInSec: data.compensatoryEventMapDetails.eventMapMaxExecutionTimeInSec,
  };

  const { items, flowItemMap, egressCountMap } = createEventMapNodes(eventMapDetails, itemEventMap, readOnly);

  const { connectors } = createEventMapEdges(eventMapDetails, itemEventMap, egressCountMap, readOnly, false);

  const rootItemId = eventMapDetails.eventMapFirstNodeId;
  const rootItem = flowItemMap[rootItemId];
  const rootItemData = itemEventMap[rootItemId];
  if (!rootItemData) {
    return { items: [], connectors: [], itemDataMap: {}, rootNodeId: '', isRootExternal: false };
  }
  if (!rootItemData.isExternal) {
    // prepend the initial connector from start btn
    connectors.unshift({
      fromNodeId: '',
      toNodeId: rootItemId,
      orientation: {
        from: Orientation.HORIZONTAL,
        to: Orientation.HORIZONTAL
      },
      text: itemEventMap[rootItemId].ingressEvent,
      displayEventVersion: itemEventMap[rootItemId].ingressEventVersion,
      tooltip: generateTooltipTextForConnector(itemEventMap[rootItemId].ingressEvent, false, false),
      config: {},
      buttons: [{
        icon: eventSchemaIcon,
        id: FlowConnectorButtonActions.VIEW_EVENT_SCHEMA,
        label: translateLabel('labels.view_event_schema'),
        height: journeyCanvasConstants.flowItemBtnHeight,
        width: journeyCanvasConstants.flowItemBtnHeight,
      }],
    });
  }

  const calculateItemPos = (item: FlowItem) => {
    const egressList = connectors.filter((c) => c.fromNodeId === item.nodeId).map((c) => flowItemMap[c.toNodeId]);
    egressList.forEach((i) => {
      if (!i.x || !i.y) {
        const { x, y } = findEmptyPositionOnCanvas(items, item === rootItem && rootItemData.isExternal, item);
        i.x = x;
        i.y = y;
      }
      calculateItemPos(i);
    });
  };
  rootItem.isRoot = true;
  if (!rootItem.x || !rootItem.y) {
    rootItem.x = rootItemData.isExternal ? journeyCanvasConstants.rootExternalItemPosition.x : journeyCanvasConstants.rootItemPosition.x;
    rootItem.y = rootItemData.isExternal ? journeyCanvasConstants.rootExternalItemPosition.y : journeyCanvasConstants.rootItemPosition.y;
  }
  calculateItemPos(rootItem);

  const andMergeNodeIds = new Set<string>();
  eventMapDetails.eventMapEdges?.forEach((edge) => {
    if (edge.ingressOperation === FlowItemConditions.AND && edge.egress?.[0]?.nodeId) {
      andMergeNodeIds.add(edge.egress[0].nodeId);
    }
  });
  const connectorsWithRules = applyConnectorConditionalExpressionRules(
    connectors,
    andMergeNodeIds,
    items.map(({ nodeId, conditionText }) => ({ nodeId, conditionText })),
    itemEventMap,
    readOnly,
    false
  );

  return { items, connectors: connectorsWithRules, itemDataMap: itemEventMap, rootNodeId: rootItemId, isRootExternal: rootItemData.isExternal };
};

/**
 * @method callCreateCEMApi
 * @description Calls the create CEM API to create a compensatory event map.
 * @param {Object} options - The options for creating the CEM.
 * @param {EventMapDetails} options.cemEventMapDetails - The event map details for the compensatory event map.
 * @param {string} options.nodeId - The ID of the node.
 * @param {string} options.journeyName - The name of the journey.
 * @param {string} options.journeyStepName - The name of the journey step.
 * @param {number} options.version - The version number.
 * @param {string} options.createdBy - The name of the user who created the CEM.
 * @param {string} options.updatedBy - The name of the user who updated the CEM.
 * @param {boolean} options.readOnly - if canvas is in view mode
 * @returns {Promise<Object>} - A promise that resolves to an object containing the items, connectors, itemDataMap, and isSuccess properties.
 */
export const callCreateCEMApi = async ({
  cemEventMapDetails,
  nodeId,
  journeyName,
  journeyStepName,
  version,
  readOnly
}: {
  cemEventMapDetails: EventMapDetails,
  nodeId: string,
  journeyName: string,
  journeyStepName: string,
  version: number,
  readOnly: boolean
}) => {
  const journeyData: CreateCEMRequest = {
    journeyName,
    stepName: journeyStepName,
    nodeId,
    version,
    isCompensateAction: true,
    compensatoryEventMapDetails: cemEventMapDetails
  };
  // create compensatory event map api call
  const resp = await createCompensatoryEventMap(journeyData);
  if (resp) {
    const { items, connectors, itemDataMap } = prepareCEMFromData(resp, readOnly);
    const cemName = resp.compensatoryEventMapDetails.eventMapName;

    return { items, connectors, itemDataMap, cemName };
  }
  return {
    items: undefined,
    connectors: undefined,
    itemDataMap: {},
    cemName: ''
  };
};

/**
 * @method callUpdateCEMApi
 * @description Calls the update CEM API to update a compensatory event map.
 * @param {Object} options - The options for updating the CEM.
 * @param {EventMapDetails} options.cemEventMapDetails - The event map details for the compensatory event map.
 * @param {FlowItemDataMap} options.flowItemDataMap - The flow item data map.
 * @param {string} options.nodeId - The ID of the node.
 * @param {string} options.journeyName - The name of the journey.
 * @param {string} options.cemRootNodeId - The ID of the CEM root node.
 * @param {string} options.cemName - The name of the compensatory event map.
 * @param {string} options.journeyStepName - The name of the journey step.
 * @param {number} options.version - The version number.
 * @param {string} options.createdBy - The name of the user who created the CEM.
 * @param {string} options.updatedBy - The name of the user who updated the CEM.
 * @param {boolean} options.readOnly - if canvas is in view mode
 * @returns {Promise<Object>} - A promise that resolves to an object containing the items, connectors, itemDataMap, and isSuccess properties.
 */
export const callUpdateCEMApi = async ({
  cemEventMapDetails,
  flowItemDataMap,
  journeyName,
  cemRootNodeId = '',
  journeyStepName,
  version,
  readOnly,
}: {
  cemEventMapDetails: EventMapDetails,
  flowItemDataMap: FlowItemDataMap,
  journeyName: string,
  cemRootNodeId?: string,
  journeyStepName: string,
  version: number,
  readOnly: boolean
}) => {
  const compensatoryEventMapData: CreateCEMRequest = {
    nodeId: cemRootNodeId,
    compensatoryEventMapName: flowItemDataMap[cemRootNodeId].compensatoryEventMapName ?? '',
    journeyName,
    stepName: journeyStepName,
    version,
    isCompensateAction: true,
    compensatoryEventMapDetails: {
      eventMapNodes: cemEventMapDetails.eventMapNodes,
      eventMapEdges: cemEventMapDetails.eventMapEdges,
      eventMapMaxExecutionTimeInSec: cemEventMapDetails.eventMapMaxExecutionTimeInSec,
      eventMapName: cemEventMapDetails.eventMapName,
    }
  };
  const resp = await updateCompensatoryEventMap(compensatoryEventMapData);
  if (resp) {
    const { items, connectors, itemDataMap } = prepareCEMFromData(resp, readOnly);

    return { items, connectors, itemDataMap };
  }
  return {
    items: undefined,
    connectors: undefined,
    itemDataMap: {},
    isSuccess: false
  };
};

/**
 * @method  cancelCEMHandler
 * @description Cancels the compensatory event map by dispatching an action to cancel it.
 * @param {Dispatch<UpdateActions>} dispatch - A dispatch function from the Redux store to dispatch the update action.
 * @returns {void}
 */
export const cancelCEMHandler = (dispatch: Dispatch<UpdateActions>) => {
  dispatch({
    type: Actions.CANCEL_COMPENSATORY_EVENT_MAP
  });
};

/**
 * @method deleteCompensatoryEventMap
 * @description deleting the compensatory event map
 */
export const deleteCompensatoryEventMap = (
  {
    loader,
    toastMessage,
    translate,
    dispatch,
    flowItemDataMap,
    cemRootNodeId,
    journeyName,
    journeyStepName,
    journeyVersion
  }: {
    loader: LoaderHook,
    toastMessage: ToastMessageHook,
    translate: TranslateHook,
    dispatch: Dispatch<UpdateActions>,
    flowItemDataMap: FlowItemDataMap,
    cemRootNodeId: string | undefined,
    journeyName: string,
    journeyStepName: string,
    journeyVersion: number
  }
) => {
  const cemName = flowItemDataMap[cemRootNodeId ?? ''].compensatoryEventMapName;
  const nodeId = cemRootNodeId ?? '';
  if (cemName) {
    loader.show();
    (async () => {
      const cemRes = await deleteCompensatoryMap(journeyName, journeyVersion, journeyStepName, nodeId, cemName);
      if (cemRes) {
        loader.hide();
        toastMessage.success(translate('journey_builder_component.title_toast_success'), translate('journey_builder_component.msg_cem_delete_success'));
        dispatch({ type: Actions.DELETE_COMPENSATORY_EVENT_MAP });
      } else {
        loader.hide();
        toastMessage.failure(translate('journey_builder_component.title_toast_error'), translate('journey_builder_component.msg_cem_delete_error'));
      }
    })();
  }
};

/**
 * @method deleteCompensatoryEventMapHandler
 * @description Handles deleteCompensatoryEventMap
 */
export const deleteCompensatoryEventMapHandler = (dispatch: Dispatch<UpdateActions>, flowItemDataMap: FlowItemDataMap, cemRootNodeId?: string | undefined) => {
  const cem = flowItemDataMap[cemRootNodeId ?? ''].compensatoryEventMapName;
  if (cem) {
    dispatch({ type: Actions.NOTIFY_DELETE_CEM, payload: { name: cem } });
  }
};

/**
 * @method saveCompensatoryEventMap
 * @description check whether to save compensatory event map or cancel save
 * @returns Actions.SAVE_COMPENSATORY_EVENT_MAP OR Actions.CANCEL_COMPENSATORY_EVENT_MAP
 */
export const saveCompensatoryEventMap = async (
  {
    loader,
    toastMessage,
    translate,
    dispatch,
    flowItems,
    flowConnectors,
    compensatoryEventMap,
    flowItemDataMap,
    cemRootNodeId,
    journeyName,
    journeyStepName,
    version,
    maxExecutionTime,
    uniqueIdentifiers,
    payloadTransformations,
    readOnly
  }: {
    loader: LoaderHook,
    toastMessage: ToastMessageHook,
    translate: TranslateHook,
    dispatch: Dispatch<UpdateActions>,
    flowItems: FlowItem[],
    flowConnectors: FlowConnector[],
    compensatoryEventMap: CompensatoryEventMap | undefined,
    flowItemDataMap: FlowItemDataMap,
    cemRootNodeId: string | undefined,
    journeyName: string,
    journeyStepName: string,
    version: number,
    maxExecutionTime: number,
    uniqueIdentifiers: string[] | undefined,
    payloadTransformations: PayloadTransformation[],
    readOnly: boolean,
  }
) => {
  const existingCEM = compensatoryEventMap;
  if (!existingCEM) {
    return;
  }
  // Check if condExpr data has changed in flowItemDataMap
  const hasCondExprChanges = Object.keys(flowItemDataMap).some((nodeId) => {
    const currentCondExpr = flowItemDataMap[nodeId]?.condExpr;
    // If there's any condExpr data, consider it a change since existingCEM doesn't track this
    return currentCondExpr && Object.keys(currentCondExpr).length > 0;
  });

  if (_.isEqual(existingCEM.flowItems, flowItems.slice(1)) && _.isEqual(existingCEM.flowConnectors, flowConnectors) && !hasCondExprChanges) {
    // do nothing
    dispatch({ type: Actions.CANCEL_COMPENSATORY_EVENT_MAP });
    return;
  }

  let isSuccess = false;
  let localFlowItemDataMap: FlowItemDataMap = {};
  let cemName = flowItemDataMap[cemRootNodeId ?? ''].compensatoryEventMapName;
  const nodeId = cemRootNodeId ?? '';
  const cemEventMapDetails = createEventMapDetail(flowItems.slice(1), flowConnectors.slice(1), flowItemDataMap, maxExecutionTime, uniqueIdentifiers?.toString(), payloadTransformations);
  if (!cemName) {
    if (flowItems.length <= 1) {
      return;
    }
    // create compensatory event map api call
    loader.show();
    const resp = await callCreateCEMApi({
      cemEventMapDetails,
      journeyName,
      journeyStepName,
      nodeId,
      version,
      readOnly
    });
    localFlowItemDataMap = resp.itemDataMap;
    cemName = resp.cemName;
    isSuccess = !!resp.cemName;
    loader.hide();
  } else if (flowItems.length > 1) {
    loader.show();
    // update compensatory event map api call
    const resp = await callUpdateCEMApi({
      cemEventMapDetails,
      flowItemDataMap,
      journeyName,
      journeyStepName,
      version,
      cemRootNodeId,
      readOnly
    });

    localFlowItemDataMap = resp.itemDataMap;
    isSuccess = resp.isSuccess ?? true;
    loader.hide();
  } else {
    loader.show();
    // cem is empty delete it
    const resp = await deleteCompensatoryMap(journeyName, version, journeyStepName, nodeId, cemName);
    if (resp) {
      dispatch({ type: Actions.DELETE_COMPENSATORY_EVENT_MAP });
      loader.hide();
      return;
    }
  }
  if (isSuccess) {
    toastMessage.success(translate('journey_builder_component.title_toast_success'), translate('journey_builder_component.msg_save_cem_success'));
    dispatch({
      type: Actions.SAVE_COMPENSATORY_EVENT_MAP,
      payload: {
        itemDataMap: localFlowItemDataMap,
        cemName,
        nodeId
      }
    });
    return;
  }
  toastMessage.failure(translate('journey_builder_component.title_toast_error'), translate('journey_builder_component.msg_save_cem_failed'));
};
